// src/pages/healthcare/consultation/consultationComponents/AdvancedSettingsDialog.tsx
import React, { useState, useEffect } from "react";
import { Modal, Tabs, Select, Slider, Button, Switch, Space, Typography, Divider } from "antd";
import { AudioOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;
const { Text } = Typography;

/**
 * What Apply emits. `audioQuality` has no consumer in the audio path — the consult pages
 * deliberately drop it rather than parking it in state (see `utils/consult/audioSettings`).
 */
export interface AdvancedSettingsPayload {
	voiceThreshold: number;
	silenceTimeout: number;
	audioQuality: string;
	noiseSuppression: boolean;
	echoCancellation: boolean;
	autoGainControl: boolean;
}

interface AdvancedSettingsDialogProps {
	visible: boolean;
	onHide: () => void;
	cameras: any[];
	selectedCamera: string;
	microphones: any[];
	selectedMicrophone: string;
	// Called on Apply when either device changed; resolving false keeps the dialog open with nothing applied.
	onDevicesApply?: (cameraId: string, micId: string) => Promise<boolean>;
	showCamera?: boolean;
	voiceThreshold?: number;
	silenceTimeout?: number;
	audioQuality?: string;
	noiseSuppression?: boolean;
	echoCancellation?: boolean;
	autoGainControl?: boolean;
	/** Fires on Apply only — never on an individual control change. */
	onSettingsChange?: (settings: AdvancedSettingsPayload) => void;
}

const AdvancedSettingsDialog: React.FC<AdvancedSettingsDialogProps> = ({
	visible,
	onHide,
	cameras,
	selectedCamera,
	microphones,
	selectedMicrophone,
	onDevicesApply,
	showCamera = true,
	voiceThreshold = 4,
	silenceTimeout = 400,
	audioQuality = "medium",
	noiseSuppression = true,
	echoCancellation = true,
	autoGainControl = true,
	onSettingsChange,
}) => {
	const [localVoiceThreshold, setLocalVoiceThreshold] = useState<number>(voiceThreshold);
	const [localSilenceTimeout, setLocalSilenceTimeout] = useState<number>(silenceTimeout);
	const [localAudioQuality, setLocalAudioQuality] = useState<string>(audioQuality);
	const [localNoiseSuppression, setLocalNoiseSuppression] = useState<boolean>(noiseSuppression);
	const [localEchoCancellation, setLocalEchoCancellation] = useState<boolean>(echoCancellation);
	const [localAutoGainControl, setLocalAutoGainControl] = useState<boolean>(autoGainControl);
	const [stagedCamera, setStagedCamera] = useState<string>(selectedCamera);
	const [stagedMicrophone, setStagedMicrophone] = useState<string>(selectedMicrophone);
	const [applying, setApplying] = useState<boolean>(false);

	useEffect(() => setStagedCamera(selectedCamera), [selectedCamera]);
	useEffect(() => setStagedMicrophone(selectedMicrophone), [selectedMicrophone]);

	// A staged device that has since been unplugged falls back to the page's selection.
	const cameraValue = cameras.some((cam) => cam.deviceId === stagedCamera) ? stagedCamera : selectedCamera;
	const microphoneValue = microphones.some((mic) => mic.deviceId === stagedMicrophone) ? stagedMicrophone : selectedMicrophone;

	useEffect(() => {
		setLocalVoiceThreshold(voiceThreshold);
		setLocalSilenceTimeout(silenceTimeout);
		setLocalAudioQuality(audioQuality);
		setLocalNoiseSuppression(noiseSuppression);
		setLocalEchoCancellation(echoCancellation);
		setLocalAutoGainControl(autoGainControl);
	}, [voiceThreshold, silenceTimeout, audioQuality, noiseSuppression, echoCancellation, autoGainControl]);

	const audioQualityOptions = [
		{ label: "Low", value: "low" },
		{ label: "Medium", value: "medium" },
		{ label: "High", value: "high" },
		{ label: "Auto", value: "auto" },
	];

	const applySettings = async () => {
		const devicesChanged = (showCamera && cameraValue !== selectedCamera) || microphoneValue !== selectedMicrophone;
		if (devicesChanged && onDevicesApply) {
			setApplying(true);
			const applied = await onDevicesApply(showCamera ? cameraValue : selectedCamera, microphoneValue).finally(() => setApplying(false));
			if (!applied) return;
		}
		if (onSettingsChange) {
			onSettingsChange({
				voiceThreshold: localVoiceThreshold,
				silenceTimeout: localSilenceTimeout,
				audioQuality: localAudioQuality,
				noiseSuppression: localNoiseSuppression,
				echoCancellation: localEchoCancellation,
				autoGainControl: localAutoGainControl,
			});
		}
		onHide();
	};

	return (
		<Modal
			title="Advanced Settings"
			open={visible}
			onCancel={() => !applying && onHide()}
			width={640}
			footer={[
				<Button key="cancel" onClick={onHide} disabled={applying} data-testid="consultation-settings-cancel">
					Cancel
				</Button>,
				<Button key="apply" type="primary" onClick={applySettings} loading={applying} data-testid="consultation-settings-apply">
					Apply
				</Button>,
			]}
		>
			<Tabs defaultActiveKey="1">
				<TabPane
					tab={
						<span>
							<AudioOutlined /> Device Settings
						</span>
					}
					key="1"
				>
					<Space direction="vertical" style={{ width: "100%" }} size="large">
						{showCamera && (
							<div>
								<Text strong>Camera</Text>
								<Select
									style={{ width: "100%", marginTop: 8 }}
									value={cameraValue}
									onChange={setStagedCamera}
									options={cameras.map((cam) => ({
										label: cam.label || cam.deviceId,
										value: cam.deviceId,
									}))}
									placeholder="Select a Camera"
									data-testid="consultation-settings-camera"
								/>
							</div>
						)}
						<div>
							<Text strong>Microphone</Text>
							<Select
								style={{ width: "100%", marginTop: 8 }}
								value={microphoneValue}
								onChange={setStagedMicrophone}
								options={microphones.map((mic) => ({
									label: mic.label || mic.deviceId,
									value: mic.deviceId,
								}))}
								placeholder="Select a Microphone"
								data-testid="consultation-settings-microphone"
							/>
						</div>
					</Space>
				</TabPane>

				<TabPane
					tab={
						<span>
							<AudioOutlined /> Audio Settings
						</span>
					}
					key="2"
				>
					<Space direction="vertical" style={{ width: "100%" }} size="large">
						<div>
							<Text strong>Voice Threshold (Sensitivity)</Text>
							<Slider value={localVoiceThreshold} onChange={setLocalVoiceThreshold} min={1} max={10} step={0.5} marks={{ 1: "1", 5: "5", 10: "10" }} />
							<Text type="secondary" style={{ fontSize: 12 }}>
								Higher values require louder speech to trigger recording. Current: {localVoiceThreshold}
							</Text>
						</div>

						<div>
							<Text strong>Silence Timeout (ms)</Text>
							<Slider value={localSilenceTimeout} onChange={setLocalSilenceTimeout} min={100} max={1000} step={50} marks={{ 100: "100", 500: "500", 1000: "1000" }} />
							<Text type="secondary" style={{ fontSize: 12 }}>
								How long to wait after speech stops before sending audio. Current: {localSilenceTimeout}ms
							</Text>
						</div>

						<div>
							<Text strong>Audio Quality</Text>
							<Select style={{ width: "100%", marginTop: 8 }} value={localAudioQuality} options={audioQualityOptions} onChange={setLocalAudioQuality} />
							<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
								Higher quality uses more bandwidth but provides better transcription.
							</Text>
						</div>

						<Divider />

						<Space direction="vertical" style={{ width: "100%" }}>
							<Space style={{ width: "100%", justifyContent: "space-between" }}>
								<div>
									<Text strong>Noise Suppression</Text>
									<br />
									<Text type="secondary" style={{ fontSize: 12 }}>
										Reduces background noise in your audio
									</Text>
								</div>
								<Switch checked={localNoiseSuppression} onChange={setLocalNoiseSuppression} />
							</Space>

							<Space style={{ width: "100%", justifyContent: "space-between" }}>
								<div>
									<Text strong>Echo Cancellation</Text>
									<br />
									<Text type="secondary" style={{ fontSize: 12 }}>
										Reduces echo from speakers/headphones
									</Text>
								</div>
								<Switch checked={localEchoCancellation} onChange={setLocalEchoCancellation} />
							</Space>

							<Space style={{ width: "100%", justifyContent: "space-between" }}>
								<div>
									<Text strong>Auto Gain Control</Text>
									<br />
									<Text type="secondary" style={{ fontSize: 12 }}>
										Automatically adjusts microphone sensitivity
									</Text>
								</div>
								<Switch checked={localAutoGainControl} onChange={setLocalAutoGainControl} />
							</Space>
						</Space>
					</Space>
				</TabPane>
			</Tabs>
		</Modal>
	);
};

export default AdvancedSettingsDialog;
